import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  saveRoomToStorage,
  setIsProjectSwitchInitiated,
} from '@store/actionSlices/appConfig'
import { saveProjectIdentityToStorage } from '@store/actionSlices/projectIdentity'
import { RootStateTypeExtra } from '@store/types'

import {
  Project,
  selectFromResult as selectFromProjectConfigResult,
  useLazyGetConfigQuery,
} from '@api/config'

import {
  removeCacheVersion,
  setCacheVersion,
  setStockCacheVersion,
} from '@utilities/cache-version-utils'
import ResetUtil from '@utilities/reset-util'
import useDefaultRoom from '@utilities/use-default-room'

import { fetchCacheVersionFromFirebase } from '@firebaseUtil/firebase-cache-version-service'

interface ValidGetProjectDetailsReturn {
  success: true
  message?: unknown
  project: Project
}

interface InvalidGetProjectDetailsReturn {
  success: false
  message: string
  project?: unknown
}

const useProjectSwitchUtils = () => {
  const dispatch = useDispatch()
  const resetUtil = ResetUtil()

  const projectIdentity = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity
  )
  const [getProjectConfig] = useLazyGetConfigQuery()

  const { getDefaultRoom } = useDefaultRoom()

  const getProjectDetails = useCallback<
    (args: {
      projectCanonicalName: string
    }) => Promise<ValidGetProjectDetailsReturn | InvalidGetProjectDetailsReturn>
  >(
    async ({ projectCanonicalName }) => {
      const projectConfigResponse = await getProjectConfig({
        projectName: projectCanonicalName,
      })
      const { configInfo, project } = selectFromProjectConfigResult(
        projectConfigResponse
      )
      if (!project.uuid) {
        return {
          success: false,
          message: 'Project not found',
        }
      }

      if (!configInfo?.showcaseActive) {
        return {
          success: false,
          message: 'Project is not active',
        }
      }

      return {
        success: true,
        project,
      }
    },
    [getProjectConfig]
  )

  const updateRoom = useCallback<
    (args: { projectCanonicalName: string }) => Promise<void>
  >(
    async ({ projectCanonicalName }) => {
      const defaultRoom = await getDefaultRoom({
        projectName: projectCanonicalName,
        prioritizeCurrentRoom: true,
      })

      if (defaultRoom) {
        saveRoomToStorage(defaultRoom)
      }
    },
    [getDefaultRoom]
  )

  const updateProjectIdentity = useCallback<
    (args: { project: Project }) => void
  >(({ project }) => {
    const { uuid, canonicalName, title } = project
    saveProjectIdentityToStorage({
      ...projectIdentity,
      projectId: uuid,
      projectName: canonicalName,
      projectLabel: title,
      assetPort: '',
      assetOptions: {
        localImages: false,
        localVideos: false,
      },
    })
    resetUtil.resetLocalStorage()
  }, [])

  const redirectToRoute = useCallback<
    (args: {
      projectCanonicalName: string
      route: string
      routeUrlParams?: string
    }) => void
  >(({ projectCanonicalName, route, routeUrlParams }) => {
    if (route) {
      const urlParams = routeUrlParams ? `?${routeUrlParams}` : ''
      window.location.href = `/${projectCanonicalName}/${route}${urlParams}`
    }
  }, [])

  const refreshPage = useCallback(() => {
    console.log('useProjectSwitchUtils')
    // window.location.reload()
  }, [])

  const processProjectSwitch = useCallback<
    (args: {
      projectCanonicalName: string
      redirectTo?: string
      redirectUrlParams?: string
    }) => Promise<{
      status: boolean
      message?: string
    }>
  >(
    async ({ projectCanonicalName, redirectTo, redirectUrlParams = '' }) => {
      removeCacheVersion()
      const cacheVersion =
        await fetchCacheVersionFromFirebase(projectCanonicalName)
      setCacheVersion(cacheVersion.version)
      setStockCacheVersion(cacheVersion.stockCacheVersion)

      const { success, message, project } = await getProjectDetails({
        projectCanonicalName,
      })

      if (!success) {
        return {
          status: false,
          message,
        }
      }

      await updateRoom({
        projectCanonicalName,
      })

      updateProjectIdentity({
        project,
      })

      const cacheVersionAfterReset =
        await fetchCacheVersionFromFirebase(projectCanonicalName)
      setCacheVersion(cacheVersionAfterReset.version)
      setStockCacheVersion(cacheVersionAfterReset.stockCacheVersion)

      if (redirectTo) {
        redirectToRoute({
          projectCanonicalName,
          route: redirectTo,
          routeUrlParams: redirectUrlParams,
        })

        return {
          status: true,
        }
      }
      refreshPage()

      return {
        status: true,
      }
    },
    [
      projectIdentity,
      getProjectDetails,
      updateRoom,
      updateProjectIdentity,
      redirectToRoute,
      refreshPage,
    ]
  )

  const startProjectSwitch = useCallback<typeof processProjectSwitch>(
    async (...args) => {
      dispatch(setIsProjectSwitchInitiated(true))
      try {
        const result = await processProjectSwitch(...args)
        if (!result.status) {
          dispatch(setIsProjectSwitchInitiated(false))
        }
        return result
      } catch (error) {
        dispatch(setIsProjectSwitchInitiated(false))
        return {
          status: false,
          message: 'Failed to switch project',
        }
      }
    },
    [processProjectSwitch]
  )

  return {
    startProjectSwitch,
  }
}

export default useProjectSwitchUtils
